import React from 'react'
import PropTypes from 'prop-types'
import { Icon, Skeleton } from 'antd'

// Mirrors the mobile "Kartu Peserta" (QR Peserta page): dark-blue header with
// the DPLK INDOLIFE logo, red/light-blue swoosh, QR, name, then No. Peserta /
// VA BCA / VA Mandiri / VA Ina / Tanggal Kepesertaan, and a logo + swoosh footer.
// Logos are the CMS assets (logo_dplk.svg; logo_dplk_light.png is the same
// artwork with INDOLIFE recoloured light blue for the dark header).
const BLUE = '#1c3d8f'
const BLUE_TEXT = '#1a4bd2'
const LIGHT_BLUE = '#3d84e6'
const PALE_BLUE = '#dce8f8'
const RED = '#e8362d'
const PAGE_BG = '#eef2f9'
// The card is laid out at a fixed width (like the mobile screen) and scaled
// down with transform to fit narrower CMS columns, so nothing wraps or clips.
const CARD_WIDTH = 420

const styles = {
  page: {
    background: PAGE_BG,
    padding: 24,
    borderRadius: 12,
  },
  card: {
    width: CARD_WIDTH,
    background: '#fff',
    borderRadius: 22,
    boxShadow: '0 8px 28px rgba(28,61,143,0.18)',
    overflow: 'hidden',
    fontFamily: 'inherit',
  },
  header: {
    position: 'relative',
    background: BLUE,
    height: 84,
    display: 'flex',
    alignItems: 'center',
    padding: '0 22px 8px 22px',
  },
  headerLogo: {
    height: 30,
    display: 'block',
    flex: '0 0 auto',
  },
  headerDivider: {
    width: 1,
    height: 46,
    background: 'rgba(255,255,255,0.55)',
    margin: '0 16px',
    flex: '0 0 1px',
  },
  headerTitle: {
    flex: 1,
    textAlign: 'right',
    color: '#fff',
    fontWeight: 800,
    fontSize: 19,
    letterSpacing: 0.2,
    whiteSpace: 'nowrap',
  },
  headerSwoosh: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    width: '100%',
    height: 20,
    display: 'block',
  },
  qrWrap: {
    display: 'flex',
    justifyContent: 'center',
    padding: '26px 24px 18px',
  },
  qrBox: {
    borderRadius: 14,
    padding: 16,
    background: '#fff',
    border: '1px solid #edf0f5',
    boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
  },
  qr: {
    width: 180,
    height: 180,
    display: 'block',
  },
  name: {
    color: BLUE_TEXT,
    fontWeight: 700,
    fontSize: 24,
    lineHeight: 1.2,
    padding: '0 30px',
    marginBottom: 8,
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    padding: '3px 30px',
    fontSize: 13,
    lineHeight: '20px',
  },
  rowIcon: {
    width: 26,
    height: 26,
    borderRadius: '50%',
    background: '#e8f0fb',
    color: BLUE_TEXT,
    fontSize: 13,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 16,
    flex: '0 0 26px',
  },
  rowLabel: {
    flex: '0 0 130px',
    color: '#2b2b2b',
    whiteSpace: 'nowrap',
  },
  rowColon: {
    color: '#2b2b2b',
    margin: '0 10px 0 0',
  },
  rowValue: {
    color: BLUE_TEXT,
    fontWeight: 700,
    wordBreak: 'break-all',
  },
  footer: {
    position: 'relative',
    borderTop: '1px solid #e5e9f2',
    margin: '14px 16px 0',
    height: 72,
  },
  footerLogo: {
    position: 'absolute',
    left: 14,
    top: 16,
    height: 22,
    display: 'block',
  },
  footerSwoosh: {
    position: 'absolute',
    right: -16,
    bottom: 0,
    width: 275,
    height: 78,
    display: 'block',
  },
}

// Header bottom edge, traced from the mockup: light-blue band rising to the
// right, a red wedge inside it tapering to a point at the band's top edge,
// and a thin dark-blue wedge under the band at the right (dark blue above is
// the header itself).
const HeaderSwoosh = () => (
  <svg viewBox="0 0 400 20" preserveAspectRatio="none" style={styles.headerSwoosh}>
    <defs>
      <clipPath id="kp-header-wave">
        <path d="M140,20 C210,20 280,13 340,7 C365,4 385,2 400,1 L400,20 Z" />
      </clipPath>
    </defs>
    <path d="M140,20 C210,20 280,13 340,7 C365,4 385,2 400,1 L400,20 Z" fill={LIGHT_BLUE} />
    <path d="M255,20 C300,13 340,7 367,3 C338,11 310,17 290,20 Z" fill={RED} clipPath="url(#kp-header-wave)" />
    <path d="M322,20 C350,18 378,15 400,11 L400,20 Z" fill={BLUE} clipPath="url(#kp-header-wave)" />
  </svg>
)

// Footer corner, traced from the mockup (300x70 box = right 65% of the card):
// pale S-wave, light-blue S-wave, a red wedge whose right edge follows the
// dark-blue corner's edge and tapers to a point, then the dark-blue corner
// drawn last so it hides the wedge's inner side.
const FooterSwoosh = () => (
  <svg viewBox="0 0 300 70" preserveAspectRatio="none" style={styles.footerSwoosh}>
    <path d="M0,70 C20,68 35,64 46,61 C90,48 120,40 134,35 C180,20 220,9 240,7 C260,4 285,1 300,0 L300,70 Z" fill={PALE_BLUE} />
    <path d="M46,70 C90,66 120,52 134,44 C180,26 220,17 240,15 C262,12 284,9 300,7 L300,70 Z" fill={LIGHT_BLUE} />
    <path d="M130,70 C155,52 192,36 240,24 C215,33 190,50 178,70 Z" fill={RED} />
    <path d="M153,70 C185,50 215,33 240,24 C260,16 280,13 300,12 L300,70 Z" fill={BLUE} />
  </svg>
)

const Row = ({ icon, label, value }) => (
  <div style={styles.row}>
    <span style={styles.rowIcon}><Icon type={icon} /></span>
    <span style={styles.rowLabel}>{label}</span>
    <span style={styles.rowColon}>:</span>
    <span style={styles.rowValue}>{value || '-'}</span>
  </div>
)

Row.propTypes = {
  icon: PropTypes.string,
  label: PropTypes.string,
  value: PropTypes.string,
}

// Renders children at CARD_WIDTH and scales them to the container width.
class FitScale extends React.Component {
  constructor(props) {
    super(props)
    this.state = { scale: 1, height: null, offset: 0 }
    this.outer = null
    this.inner = null
    this.setOuter = (el) => { this.outer = el }
    this.setInner = (el) => { this.inner = el }
    this.measure = this.measure.bind(this)
  }

  componentDidMount() {
    this.measure()
    if (typeof window !== 'undefined' && window.ResizeObserver) {
      this.observer = new window.ResizeObserver(this.measure)
      this.observer.observe(this.outer)
      this.observer.observe(this.inner)
    } else if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.measure)
    }
  }

  componentWillUnmount() {
    if (this.observer) this.observer.disconnect()
    else if (typeof window !== 'undefined') window.removeEventListener('resize', this.measure)
  }

  measure() {
    if (!this.outer || !this.inner) return
    const width = this.outer.clientWidth
    const scale = Math.min(1, width / CARD_WIDTH)
    const height = Math.round(this.inner.offsetHeight * scale)
    const offset = Math.max(0, Math.round((width - (CARD_WIDTH * scale)) / 2))
    const { state } = this
    if (scale !== state.scale || height !== state.height || offset !== state.offset) {
      this.setState({ scale, height, offset })
    }
  }

  render() {
    const { children } = this.props
    const { scale, height, offset } = this.state
    return (
      <div ref={this.setOuter} style={{ width: '100%', height }}>
        <div
          ref={this.setInner}
          style={{
            width: CARD_WIDTH, transform: `scale(${scale})`, transformOrigin: 'top left', marginLeft: offset,
          }}
        >
          {children}
        </div>
      </div>
    )
  }
}

FitScale.propTypes = {
  children: PropTypes.node,
}

const MemberCard = ({ data, isFetching }) => {
  if (isFetching) return <Skeleton active />
  if (!data) return null

  return (
    <div style={styles.page}>
      <FitScale>
        <div style={styles.card}>
          <div style={styles.header}>
            <img src="/assets/logo_dplk_light.png" alt="DPLK Indolife" style={styles.headerLogo} />
            <div style={styles.headerDivider} />
            <div style={styles.headerTitle}>KARTU PESERTA</div>
            <HeaderSwoosh />
          </div>

          <div style={styles.qrWrap}>
            <div style={styles.qrBox}>
              {data.qr_code
                ? <img src={data.qr_code} alt="QR Peserta" style={styles.qr} />
                : <div style={{ ...styles.qr, background: '#f5f5f5' }} />}
            </div>
          </div>

          <div style={styles.name}>{data.name || '-'}</div>
          <Row icon="user" label="No. Peserta" value={data.registered_number} />
          <Row icon="credit-card" label="No. VA BCA" value={data.va_bca} />
          <Row icon="bank" label="No. VA Mandiri" value={data.va_mandiri} />
          <Row icon="wallet" label="No. VA Ina" value={data.va_ina} />
          <Row icon="calendar" label="Tanggal Kepesertaan" value={data.join_date} />

          <div style={styles.footer}>
            <img src="/assets/logo_dplk.svg" alt="DPLK Indolife" style={styles.footerLogo} />
            <FooterSwoosh />
          </div>
        </div>
      </FitScale>
    </div>
  )
}

MemberCard.propTypes = {
  data: PropTypes.object,
  isFetching: PropTypes.bool,
}

export default MemberCard
